/**
 * Share Management endpoints. Codex parity contract preserves these paths.
 *
 * Cloud note: the certificate rollout is sent as base64 JSON (not multipart)
 * because the cloud API has no multer — the connector needs the PFX bytes
 * inline. The ServerCertificatesModal is unchanged (still hands us a
 * FormData); this wrapper converts it. Every other path matches the beta.
 */

import { apiGet, apiPost, apiDelete, buildQuery } from '../client.js';

export const fetchServers          = (refresh, probe)  => apiGet(`/api/shares/servers${(refresh || probe) ? buildQuery({ ...(refresh ? { refresh: true } : {}), ...(probe ? { probe: true } : {}) }) : ''}`);
export const fetchServerStatus     = (server)         => apiGet(`/api/shares/server/${encodeURIComponent(server)}/status`);
export const fetchSharesList       = (server, refresh) => apiGet(`/api/shares/list${buildQuery({ server, ...(refresh ? { refresh: true } : {}) })}`);
export const fetchSharePermissions = (path)           => apiGet(`/api/shares/permissions${buildQuery({ path })}`);
export const addSharePermission    = (body)           => apiPost('/api/shares/permissions/add', { body });
export const removeSharePermission = (body)           => apiDelete('/api/shares/permissions/remove', { body });
export const fetchServerSessions   = (server)         => apiGet(`/api/shares/server-sessions${buildQuery({ server })}`);
export const logoffSession         = (body)           => apiPost('/api/shares/server-logoff', { body });
export const fetchShareDrives      = (server)         => apiGet(`/api/shares/drives${buildQuery({ server })}`);
export const browseShareFolders    = (server, path)   => apiGet(`/api/shares/browse-folders${buildQuery({ server, path })}`);
export const fetchShareCompanies   = ()               => apiGet('/api/shares/companies');
export const fetchShareGroups      = (company)        => apiGet(`/api/shares/groups${buildQuery({ company })}`);
export const createShareGroup      = (body)           => apiPost('/api/shares/create-group', { body });
export const createShare           = (body)           => apiPost('/api/shares/create', { body });
export const fetchAllDrives        = ()               => apiGet('/api/drives');

/* ---- Server login history (v2 module) ---- */
export const fetchServerLoginHistory  = (server, refresh) => apiGet(`/api/shares/server-login-history${buildQuery({ server, ...(refresh ? { refresh: 'true' } : {}) })}`);
export const fetchLoginBackfillStatus = (server)          => apiGet(`/api/shares/server-login-history/backfill-status${buildQuery({ server })}`);
export const startLoginBackfill       = (server)          => apiPost('/api/shares/server-login-history/backfill', { body: { server } });

/* ---- Certificate management (v2 module) ---- */
export const scanServerCertificates   = (servers)         => apiPost('/api/shares/certificates/scan', { body: { servers } });
export const certificateDryRun        = (servers)         => apiPost('/api/shares/certificates/dry-run', { body: { servers } });
export const fetchCertificateHistory  = ()                => apiGet('/api/shares/certificates/history');
export const scanAllServerCertificates = ()               => apiPost('/api/shares/certificates/scan-all');
export const fetchCertificateScanStatus = ()              => apiGet('/api/shares/certificates/scan-all/status');

async function fileToBase64(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/**
 * Accepts the same FormData the ServerCertificatesModal builds ('pfx' file,
 * 'password', 'servers' JSON string, 'updateBindings' string) and sends it
 * as base64 JSON to the connector-backed cloud route.
 */
export const rolloutCertificate = async (formData) => {
  const file = formData.get('pfx');
  const pfxBase64 = file ? await fileToBase64(file) : '';
  let servers = [];
  try { servers = JSON.parse(formData.get('servers') || '[]'); } catch { servers = []; }
  return apiPost('/api/shares/certificates/rollout', {
    body: {
      servers,
      pfxBase64,
      password: formData.get('password') || '',
      updateBindings: formData.get('updateBindings') !== 'false'
    }
  });
};
